import _ from 'lodash';

import { jsonStringify } from 'utils/json';
import { getIsNativePlatform } from 'utils/platform';

// DEV-4084: native App Insights reporter.
//
// The full App Insights browser SDK (AppInsightsAggregator) is force-disabled in the Capacitor build —
// its constructor infinite-loops and OOMs the WebView under the capacitor://localhost origin (see the
// mobile build-web.mjs OOM guard). That left native JS errors completely invisible. This reporter
// bypasses the SDK entirely: it POSTs telemetry straight to the App Insights ingestion REST API
// (`<IngestionEndpoint>/v2/track`) using the connection string injected for native under
// `window.lsNativeAiConn` (a var the SDK does NOT read, so the SDK stays off and the OOM never happens).
//
// No-op on web (the real SDK handles web) and whenever the native connection string is absent.

const CLOUD_ROLE = 'Admin Panel';
const ROLE_INSTANCE = 'native-shell';
const SEVERITY_ERROR = 3;

const parseConnectionString = conn =>
  _.fromPairs(
    String(conn || '')
      .split(';')
      .map(pair => {
        const index = pair.indexOf('=');

        return index === -1 ? null : [pair.slice(0, index).trim(), pair.slice(index + 1).trim()];
      })
      .filter(Boolean),
  );

const getNativeAiConfig = () => {
  const parsed = parseConnectionString(_.get(window, 'lsNativeAiConn'));
  const iKey = parsed.InstrumentationKey;
  const ingestionEndpoint = parsed.IngestionEndpoint;

  return iKey && ingestionEndpoint ? { iKey, ingestionEndpoint } : null;
};

const toExceptionDetails = errorOrMessage => {
  if (_.isError(errorOrMessage)) {
    return {
      typeName: errorOrMessage.name || 'Error',
      message: errorOrMessage.message || String(errorOrMessage),
      stack: errorOrMessage.stack || null,
    };
  }

  return { typeName: 'Error', message: String(errorOrMessage), stack: null };
};

const buildEnvelopeShell = ({ iKey, telemetryType }) => ({
  name: `Microsoft.ApplicationInsights.${iKey.replace(/-/g, '')}.${telemetryType}`,
  time: new Date().toISOString(),
  iKey,
  tags: {
    'ai.cloud.role': CLOUD_ROLE,
    'ai.cloud.roleInstance': ROLE_INSTANCE,
    'ai.device.type': 'Mobile',
    'ai.operation.name': _.get(window, 'location.pathname') || '',
  },
});

const buildExceptionEnvelope = ({ iKey, errorOrMessage, properties }) => {
  const { typeName, message, stack } = toExceptionDetails(errorOrMessage);

  return {
    ...buildEnvelopeShell({ iKey, telemetryType: 'Exception' }),
    data: {
      baseType: 'ExceptionData',
      baseData: {
        ver: 2,
        exceptions: [
          { typeName, message, hasFullStack: !_.isNil(stack), stack: stack || '', parsedStack: [] },
        ],
        severityLevel: SEVERITY_ERROR,
        properties: { source: ROLE_INSTANCE, ...properties },
      },
    },
  };
};

// App Insights custom-event (EventData) payloads only accept string property values, so coerce.
const toStringProperties = properties =>
  _.mapValues(properties, value => (_.isNil(value) ? '' : String(value)));

const buildEventEnvelope = ({ iKey, eventName, properties }) => ({
  ...buildEnvelopeShell({ iKey, telemetryType: 'Event' }),
  data: {
    baseType: 'EventData',
    baseData: {
      ver: 2,
      name: eventName,
      properties: { source: ROLE_INSTANCE, ...toStringProperties(properties) },
    },
  },
});

// Fire-and-forget POST to the App Insights ingestion REST API. Prefer sendBeacon: telemetry-grade,
// no CORS preflight (text/plain is a CORS-simple type), and reliable in WKWebView where fetch+keepalive
// is flaky. Fall back to a plain text/plain fetch. App Insights parses the body as JSON regardless of
// content type. Never throws — telemetry must not break the app.
const postEnvelope = (config, envelope) => {
  try {
    const url = `${config.ingestionEndpoint.replace(/\/$/, '')}/v2/track`;
    const body = jsonStringify(envelope);
    const beacon = _.get(navigator, 'sendBeacon');

    if (_.isFunction(beacon)) {
      beacon.call(navigator, url, body);

      return;
    }

    fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body }).catch(_.noop);
  } catch {
    // Telemetry must never break the app.
  }
};

/**
 * Report an error/exception to App Insights from the native shell. No-op on web (the SDK handles it)
 * and when the native connection string is absent. Fire-and-forget — never throws.
 */
export const reportNativeError = (errorOrMessage, properties = {}) => {
  if (!getIsNativePlatform()) {
    return;
  }

  const config = getNativeAiConfig();

  if (!config) {
    return;
  }

  postEnvelope(config, buildExceptionEnvelope({ iKey: config.iKey, errorOrMessage, properties }));
};

/**
 * Report a custom event to App Insights from the native shell — used to surface native-owned activity
 * (e.g. the native calling plugin's call lifecycle) that the disabled browser SDK can't see. No-op on
 * web and when the native connection string is absent. Fire-and-forget — never throws.
 */
export const reportNativeEvent = (eventName, properties = {}) => {
  if (!getIsNativePlatform()) {
    return;
  }

  const config = getNativeAiConfig();

  if (!config) {
    return;
  }

  postEnvelope(config, buildEventEnvelope({ iKey: config.iKey, eventName, properties }));
};

/**
 * Install global native error handlers (uncaught errors + unhandled promise rejections) so every native
 * JS failure reaches App Insights, not just the explicitly-reported ones. No-op on web.
 */
export const installNativeTelemetry = () => {
  if (!getIsNativePlatform() || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', event => {
    reportNativeError(event?.error || event?.message || 'Uncaught error', { handler: 'onerror' });
  });

  window.addEventListener('unhandledrejection', event => {
    reportNativeError(event?.reason || 'Unhandled rejection', { handler: 'unhandledrejection' });
  });
};
